import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Plus } from 'lucide-react';
import Toast from '../../components/Toast';
import AddProspectModal from '../../components/prospection/AddProspectModal.jsx';
import Nr2jContractLivePreviewPanel from '../../components/nr2j/Nr2jContractLivePreviewPanel.jsx';
import Nr2jContractSignatoryField from '../../components/nr2j/Nr2jContractSignatoryField.jsx';
import Nr2jOrgCombobox from '../../components/nr2j/Nr2jOrgCombobox.jsx';
import { useToast } from '../../hooks/useToast';
import apiService from '../../services/api';
import {
  NR2J_CATALOG_BILLING_TYPES,
  NR2J_VAT_OPTIONS,
  centsToEuros,
  computeQuoteLineTotals,
  eurosToCents,
  formatCentsAsEuros,
} from '../../utils/nr2jConstants';
import { createNr2jProspectFromModal } from '../../utils/nr2jCreateProspect.js';
import Nr2jContinueModal from '../../components/nr2j/Nr2jContinueModal.jsx';
import { useNr2jContinueAction } from '../../hooks/useNr2jContinueAction.js';
import {
  NR2J_CONTINUE_INTENTS,
  preflightQuoteToContract,
  requirementsFromError,
} from '../../utils/nr2jContinuePreflight.js';

function emptyLine() {
  return {
    key: `${Date.now()}-${Math.random()}`,
    catalogItemId: '',
    name: '',
    description: '',
    quantity: 1,
    unitPriceHtCents: 0,
    priceEuros: '',
    vatRateBps: 2000,
    billingType: 'ONE_TIME',
    discountMode: 'none',
    discountPercent: '',
    discountEuros: '',
  };
}

function lineToPayload(line, index) {
  const unitPriceHtCents = line.priceEuros !== ''
    ? eurosToCents(line.priceEuros)
    : Number(line.unitPriceHtCents) || 0;
  const payload = {
    catalogItemId: line.catalogItemId || null,
    sortOrder: index,
    name: line.name.trim(),
    description: line.description?.trim() || null,
    quantity: Math.max(1, Number(line.quantity) || 1),
    unitPriceHtCents,
    vatRateBps: Number(line.vatRateBps),
    billingType: line.billingType,
    discountPercentBps: null,
    discountAmountHtCents: null,
  };
  if (line.discountMode === 'percent' && line.discountPercent !== '') {
    payload.discountPercentBps = Math.round(Number(line.discountPercent) * 100);
  } else if (line.discountMode === 'fixed' && line.discountEuros !== '') {
    payload.discountAmountHtCents = eurosToCents(line.discountEuros);
  }
  return payload;
}

function publishedTemplateOptions(templates) {
  const options = [];
  for (const t of templates || []) {
    for (const v of t.versions || []) {
      if (v.publishedAt) {
        options.push({
          id: v.id,
          label: `${t.name} — v${v.version}`,
        });
      }
    }
  }
  return options;
}

export default function Nr2jContractEditor() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const presetOrgId = searchParams.get('organizationId') || '';
  const fromQuoteId = searchParams.get('quoteId') || '';
  const { toasts, toast, removeToast } = useToast();
  const {
    continueReq,
    openContinue,
    closeContinue,
    runContinueRetry,
    withActionLock,
  } = useNr2jContinueAction();
  const [quoteBootstrap, setQuoteBootstrap] = useState(null);
  const quoteBootstrapStartedRef = useRef(false);
  const quoteCreateInFlightRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [organizationId, setOrganizationId] = useState(presetOrgId);
  const [org, setOrg] = useState(null);
  const [signatoryContactId, setSignatoryContactId] = useState('');
  const [templateVersionId, setTemplateVersionId] = useState('');
  const [clientNote, setClientNote] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [lines, setLines] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createPrefill, setCreatePrefill] = useState('');

  const templateOptions = useMemo(() => publishedTemplateOptions(templates), [templates]);

  const loadMeta = useCallback(async () => {
    setLoading(true);
    try {
      const [catalogRes, templatesRes] = await Promise.all([
        apiService.listNr2jCatalog({ active: true }).catch(() => ({ items: [] })),
        apiService.listNr2jContractTemplates().catch(() => ({ templates: [] })),
      ]);
      setCatalog(catalogRes?.items || []);
      const list = templatesRes?.templates || templatesRes || [];
      setTemplates(Array.isArray(list) ? list : []);
    } catch (error) {
      toast.error(error?.message || 'Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  function resumeCreateFromQuotePayload(payload) {
    return bootstrapCreateFromQuote({
      fromContinue: true,
      org: payload?.org,
      contacts: payload?.contacts,
      signatoryContactId: payload?.signatoryContactId,
    });
  }

  async function createFromQuoteCore(signatoryContactId, options = {}) {
    if (quoteCreateInFlightRef.current && !options.fromContinue) return;
    quoteCreateInFlightRef.current = true;
    try {
      const payload = signatoryContactId ? { signatoryContactId } : {};
      const res = await apiService.createNr2jContractFromQuote(fromQuoteId, payload);
      const contract = res?.contract || res;
      if (contract?.id) {
        toast.success(contract.number ? `Contrat créé — ${contract.number}` : 'Contrat créé');
        navigate(`/nr2j/contrats/${contract.id}`, { replace: true });
      }
    } finally {
      quoteCreateInFlightRef.current = false;
    }
  }

  async function bootstrapCreateFromQuote(options = {}) {
    const run = async () => {
      setBusy(true);
      let organization = options.org ?? quoteBootstrap?.org ?? null;
      let contacts = options.contacts ?? quoteBootstrap?.contacts ?? organization?.contacts ?? [];
      try {
        if (!options.fromContinue && !quoteBootstrap?.quote) {
          const quoteRes = await apiService.getNr2jQuote(fromQuoteId);
          const quote = quoteRes?.quote || quoteRes;
          const orgDetail = await apiService.getNr2jOrganization(quote.organizationId);
          organization = orgDetail?.organization || orgDetail;
          contacts = organization?.contacts || orgDetail?.contacts || [];
          setQuoteBootstrap({ quote, org: organization, contacts });
        }
        const quote = quoteBootstrap?.quote
          || (await apiService.getNr2jQuote(fromQuoteId))?.quote;
        const cv = quote?.currentVersion;
        const orgCtx = options.org ?? organization;
        const contactsCtx = options.contacts ?? contacts;

        if (options.org) {
          setQuoteBootstrap((prev) => ({
            ...prev,
            org: options.org,
            contacts: options.contacts || prev?.contacts,
          }));
        }

        if (!options.fromContinue) {
          const pre = preflightQuoteToContract({
            org: orgCtx,
            contacts: contactsCtx,
            quote,
            version: cv,
          });
          if (pre) {
            openContinue({
              ...pre,
              intent: NR2J_CONTINUE_INTENTS.CONTRACT_FROM_QUOTE_EDITOR,
              quoteId: fromQuoteId,
              versionId: cv?.id,
            }, (payload) => resumeCreateFromQuotePayload(payload));
            return;
          }
        }
        await createFromQuoteCore(
          options.signatoryContactId,
          { fromContinue: Boolean(options.fromContinue) },
        );
      } catch (error) {
        const existingId =
          error?.data?.error?.details?.existingContractId
          || error?.response?.data?.error?.details?.existingContractId
          || error?.details?.existingContractId;
        if (existingId) {
          toast.success('Contrat déjà existant pour ce devis');
          navigate(`/nr2j/contrats/${existingId}`, { replace: true });
          return;
        }
        const orgCtx = options.org ?? organization;
        const contactsCtx = options.contacts ?? contacts;
        const req = requirementsFromError(error, {
          intent: NR2J_CONTINUE_INTENTS.CONTRACT_FROM_QUOTE_EDITOR,
          org: orgCtx,
          contacts: contactsCtx,
          quoteId: fromQuoteId,
        });
        if (req) {
          openContinue(req, (payload) => resumeCreateFromQuotePayload(payload));
          return;
        }
        toast.error(error?.message || 'Création depuis devis impossible');
      } finally {
        setBusy(false);
      }
    };
    if (options.fromContinue) {
      await run();
      return;
    }
    await withActionLock(run);
  }

  useEffect(() => {
    if (!fromQuoteId) return undefined;
    if (quoteBootstrapStartedRef.current) return undefined;
    quoteBootstrapStartedRef.current = true;
    bootstrapCreateFromQuote();
    return undefined;
  }, [fromQuoteId]);

  useEffect(() => {
    if (!organizationId) {
      setOrg(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const detail = await apiService.getNr2jOrganization(organizationId);
        if (!cancelled) setOrg(detail?.organization || detail);
      } catch {
        if (!cancelled) setOrg(null);
      }
    })();
    return () => { cancelled = true; };
  }, [organizationId]);

  function selectOrganization(nextOrg) {
    const id = nextOrg?.id || '';
    setOrganizationId(id);
    setOrg(nextOrg || null);
    setSignatoryContactId('');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set('organizationId', id);
      else next.delete('organizationId');
      next.delete('quoteId');
      return next;
    }, { replace: true });
  }

  function mergeOrgContacts(updater) {
    setOrg((prev) => updater(prev));
  }

  function buildPayload() {
    return {
      organizationId,
      templateVersionId: templateVersionId || null,
      signatoryContactId,
      notes: {
        clientNote: clientNote || null,
        internalNote: internalNote || null,
      },
      lines: lines.map(lineToPayload),
    };
  }

  async function saveDraft() {
    if (!organizationId) {
      toast.error('Sélectionnez une société');
      return;
    }
    if (!signatoryContactId) {
      toast.error('Sélectionnez un signataire');
      return;
    }
    setBusy(true);
    try {
      const res = await apiService.createNr2jContract(buildPayload());
      const contract = res?.contract || res;
      toast.success(contract.number ? `Brouillon créé — ${contract.number}` : 'Brouillon créé');
      navigate(`/nr2j/contrats/${contract.id}`, { replace: true });
    } catch (error) {
      toast.error(error?.message || 'Enregistrement impossible');
    } finally {
      setBusy(false);
    }
  }

  function addCatalogLine(itemId) {
    const item = catalog.find((c) => c.id === itemId);
    if (!item) return;
    setLines((prev) => [
      ...prev,
      {
        ...emptyLine(),
        catalogItemId: item.id,
        name: item.name,
        description: item.description || '',
        priceEuros: centsToEuros(item.unitPriceHtCents),
        unitPriceHtCents: item.unitPriceHtCents,
        vatRateBps: item.vatRateBps,
        billingType: item.billingType,
      },
    ]);
  }

  const continueModal = (
    <Nr2jContinueModal
      open={Boolean(continueReq)}
      requirements={continueReq?.requirements}
      templateOptions={templateOptions}
      onClose={closeContinue}
      onSaved={async (payload) => {
        if (payload?.org) {
          setQuoteBootstrap((prev) => ({
            ...prev,
            org: payload.org,
            contacts: payload.contacts || prev?.contacts,
          }));
        }
        const retry = continueReq?.retry;
        closeContinue();
        if (retry) await runContinueRetry(retry, payload);
      }}
    />
  );

  if (fromQuoteId) {
    return (
      <>
        <div className="p-8 text-sm text-gray-500" data-testid="nr2j-contract-from-quote">
          {continueReq ? 'Complétez les informations pour créer le contrat…' : 'Création du contrat depuis le devis…'}
        </div>
        {continueModal}
        {toasts.map((item) => (
          <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
        ))}
      </>
    );
  }

  return (
    <div className="space-y-6" data-testid="nr2j-contract-editor">
      <div className="sticky top-0 z-20 -mx-1 space-y-2 border-b border-gray-200 bg-white/95 px-1 py-3 backdrop-blur">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <Link to="/nr2j/contrats" className="inline-flex items-center gap-1 text-sm text-indigo-700">
              <ArrowLeft className="h-4 w-4" /> Retour aux contrats
            </Link>
            <h1 className="mt-2 text-2xl font-bold text-gray-900">Nouveau contrat</h1>
            <p className="text-sm text-gray-600">Brouillon — signature électronique OTP après envoi.</p>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="nr2j-contract-sticky-actions">
            <button
              type="button"
              disabled={busy || loading}
              onClick={saveDraft}
              className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              Enregistrer
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Chargement…</p>
      ) : (
        <div className="grid gap-6 xl:grid-cols-3">
          <div className="space-y-6 xl:col-span-2">
            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Société & signataire</h2>
              <div className="mt-3 block text-sm">
                <span className="text-gray-500">Société *</span>
                <div className="mt-1">
                  <Nr2jOrgCombobox
                    value={organizationId}
                    selectedOrg={org}
                    disabled={busy}
                    onChange={(next) => selectOrganization(next)}
                    onCreateCompany={(prefill) => {
                      setCreatePrefill(prefill || '');
                      setCreateOpen(true);
                    }}
                  />
                </div>
              </div>
              <Nr2jContractSignatoryField
                org={org}
                signatoryContactId={signatoryContactId}
                onSignatoryContactIdChange={setSignatoryContactId}
                onOrgContactsUpdated={mergeOrgContacts}
                disabled={busy}
              />
              <label className="mt-3 block text-sm">
                <span className="text-gray-500">Modèle de contrat</span>
                <select
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={templateVersionId}
                  onChange={(e) => setTemplateVersionId(e.target.value)}
                >
                  <option value="">— Modèle par défaut —</option>
                  {templateOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  ))}
                </select>
              </label>
            </section>

            <section className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-4 text-sm text-indigo-950">
              <p className="font-medium">Durée contractuelle</p>
              <p className="mt-1 text-indigo-900/90">
                Sans engagement — résiliation possible avec un préavis écrit d’un mois
                (détaillé dans le PDF contrat).
              </p>
            </section>

            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Lignes d’offre</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                <select
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  defaultValue=""
                  onChange={(e) => {
                    if (e.target.value) {
                      addCatalogLine(e.target.value);
                      e.target.value = '';
                    }
                  }}
                >
                  <option value="">Ajouter depuis le catalogue…</option>
                  {catalog.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.code} — {item.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setLines((prev) => [...prev, emptyLine()])}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <Plus className="h-4 w-4" /> Ligne libre
                </button>
              </div>
              <div className="mt-4 space-y-4">
                {lines.length === 0 ? (
                  <p className="text-sm text-gray-500">Aucune ligne.</p>
                ) : (
                  lines.map((line, idx) => {
                    const lineTotals = computeQuoteLineTotals(lineToPayload(line, 0));
                    return (
                      <div key={line.key} className="rounded-lg border border-gray-100 p-3">
                        <div className="grid gap-2 sm:grid-cols-2">
                          <label className="block text-sm sm:col-span-2">
                            <span className="text-gray-500">Désignation *</span>
                            <input
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.name}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx ? { ...l, name: e.target.value } : l
                              )))}
                            />
                          </label>
                          <label className="block text-sm">
                            <span className="text-gray-500">Qté</span>
                            <input
                              type="number"
                              min="1"
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.quantity}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx ? { ...l, quantity: e.target.value } : l
                              )))}
                            />
                          </label>
                          <label className="block text-sm">
                            <span className="text-gray-500">Prix unitaire HT (€)</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.priceEuros}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx ? { ...l, priceEuros: e.target.value } : l
                              )))}
                            />
                          </label>
                          <label className="block text-sm">
                            <span className="text-gray-500">TVA</span>
                            <select
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.vatRateBps}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx ? { ...l, vatRateBps: Number(e.target.value) } : l
                              )))}
                            >
                              {NR2J_VAT_OPTIONS.map((v) => (
                                <option key={v.bps} value={v.bps}>{v.label}</option>
                              ))}
                            </select>
                          </label>
                          <label className="block text-sm">
                            <span className="text-gray-500">Facturation</span>
                            <select
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.billingType}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx ? { ...l, billingType: e.target.value } : l
                              )))}
                            >
                              {NR2J_CATALOG_BILLING_TYPES.map((t) => (
                                <option key={t.id} value={t.id}>{t.label}</option>
                              ))}
                            </select>
                          </label>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
                          <span>
                            Ligne TTC : {formatCentsAsEuros(lineTotals.lineTtcCents)} €
                          </span>
                          <button
                            type="button"
                            className="text-rose-700 hover:underline"
                            onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}
                          >
                            Supprimer
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </section>

            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <label className="block text-sm">
                <span className="text-gray-500">Note client</span>
                <textarea
                  rows={2}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={clientNote}
                  onChange={(e) => setClientNote(e.target.value)}
                />
              </label>
              <label className="mt-3 block text-sm">
                <span className="text-gray-500">Note interne</span>
                <textarea
                  rows={2}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={internalNote}
                  onChange={(e) => setInternalNote(e.target.value)}
                />
              </label>
            </section>
          </div>

          <aside className="space-y-4">
            <Nr2jContractLivePreviewPanel lines={lines} lineToPayload={lineToPayload} />
          </aside>
        </div>
      )}

      <AddProspectModal
        open={createOpen}
        busy={createBusy}
        title="Créer une nouvelle société"
        initialBusinessName={createPrefill}
        submitLabel="Créer la société"
        onClose={() => {
          setCreateOpen(false);
          setCreatePrefill('');
        }}
        onSubmit={async (payload) => {
          setCreateBusy(true);
          try {
            const { organization } = await createNr2jProspectFromModal(apiService, payload);
            if (!organization?.id) {
              throw new Error('Société créée mais identifiant manquant');
            }
            const detail = await apiService.getNr2jOrganization(organization.id);
            selectOrganization(detail?.organization || detail);
            setCreateOpen(false);
            setCreatePrefill('');
            toast.success('Société créée');
          } catch (error) {
            toast.error(error?.message || 'Création impossible');
          } finally {
            setCreateBusy(false);
          }
        }}
      />

      {continueModal}

      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}
    </div>
  );
}
